import { hasActiveSchoolLicense, hasExpiredSchoolLicense, hasPaidHistory, isChurned } from "./mailchimpTags";
import { isBackToSchoolPreviewActive } from "./backToSchoolPreview";
import { isFallWinbackPreviewActive } from "./campaignEntitlements";
import { isSubscriptionPaused } from "./subscriptionPauseStatus";
import { isTrialExpired } from "./trialStatus";
import { DEFAULT_PRICING_DATA } from "./pricingDefaults";
import automationRoutineTimelineImage from "assets/img/landing-v2/automation-routine-timeline.png";
import teacherHubScreenshot from "assets/img/legacy-schedule-july-2026-conversion-teacher-hub.png";

export const PRODUCT_ACCESS_GATE_VARIANTS = {
  PAUSED: "paused",
  SCHOOL_EXPIRED: "school_expired",
  CHURNED: "churned",
  TRIAL_EXPIRED: "trial_expired",
};

const SUPPORT_EMAIL = "support@lessonlaunchpad.com";
// Keep in sync with WHATS_NEW_PAGE_PATH in config/whatsNewContent.js
const WHATS_NEW_PATH = "/whats-new";
const PRICING_PATH = "/pricing";
const SUBSCRIPTION_PATH = "/account/subscription";
const SCHOOL_ADMIN_PATH = "/for-administrators";
const CONTACT_PATH = "/contact";

const SHARED_STORIES = [
  {
    id: "automations",
    title: "It runs more of your class for you",
    body:
      "Timers start automatically. Reminders appear when they should. Music changes. Classes switch. Your screen keeps the routine moving without you babysitting it.",
    image: {
      src: automationRoutineTimelineImage,
      alt: "Automated classroom routine timeline in Lesson Launchpad",
    },
  },
  {
    id: "teacher-hub",
    title: "Your whole day lives in one place",
    body:
      "The new Teacher Hub brings together your classes, schedules, automations, events, and content — so setup is organized instead of scattered.",
    image: {
      src: teacherHubScreenshot,
      alt: "Teacher Hub with Classes, Automations, Schedules, Content Manager, and Events",
    },
  },
  {
    id: "present-screen",
    title: "Managing your content is finally easier",
    body:
      "Organize, find, schedule, and reuse your classroom content without digging through individual classes.",
    image: {
      src: "/images/whats-new/whats-new-content-manager-after.png",
      alt: "Updated Content Manager and Present Screen experience",
    },
  },
];

function monthlyPriceLine() {
  return `Plans start at $${DEFAULT_PRICING_DATA.monthlyPrice}/month or $${DEFAULT_PRICING_DATA.yearlyPrice}/year.`;
}

function basePresentation(overrides) {
  return {
    signOutLabel: "Sign out",
    signOutPrompt: "Not the account you meant to use?",
    reactivationEyebrow: "Welcome back",
    stories: SHARED_STORIES,
    supportEmail: SUPPORT_EMAIL,
    showPricingInClosing: true,
    pricingLine: monthlyPriceLine(),
    ...overrides,
  };
}

export function hasProductAccess(user, options = {}) {
  if (!user?.uid) return false;
  if (isSubscriptionPaused(user, options)) return false;
  if (hasActiveSchoolLicense(user)) return true;
  if (user.premium === true) return true;
  if (user.trial === true && !isTrialExpired(user, options)) return true;
  if (isFallWinbackPreviewActive(user, options)) return true;
  if (isBackToSchoolPreviewActive(user, options)) return true;
  return false;
}

/**
 * Centralized copy + CTA presentation for the logged-in no-access gate.
 * Access entitlement rules stay in hasProductAccess / pause helpers.
 */
export function getProductAccessGatePresentation(user, options = {}) {
  const resumeDateLabel = options.resumeDateLabel || "your resume date";

  if (isSubscriptionPaused(user, options)) {
    return basePresentation({
      variant: PRODUCT_ACCESS_GATE_VARIANTS.PAUSED,
      contextEyebrow: "You're signed in",
      contextHeadline: "Your subscription is paused.",
      contextMessage: `Billing is on hold until ${resumeDateLabel}. Your classes and content are saved, and nothing is broken — access returns when your subscription resumes, or you can manage it now.`,
      reactivationHeadline: "Your classroom is still here.",
      reactivationMessage:
        "While billing is paused, you can review what’s new in Lesson Launchpad or manage your subscription. You do not need to start a new plan if you are waiting for your resume date.",
      primaryCta: {
        label: "Manage subscription",
        path: SUBSCRIPTION_PATH,
      },
      secondaryCta: {
        label: "See what's new",
        path: WHATS_NEW_PATH,
      },
      tertiaryCta: null,
      closingHeadline: "Need to make a change?",
      closingPrimaryCta: {
        label: "Manage subscription",
        path: SUBSCRIPTION_PATH,
      },
      closingSecondaryCta: {
        label: "See what's new",
        path: WHATS_NEW_PATH,
      },
      showPricingInClosing: false,
      pricingLine: null,
      // Legacy fields
      headline: "Your subscription is paused.",
      message: `Billing is on hold until ${resumeDateLabel}. Your classes and content are saved.`,
      ctaLabel: "Manage subscription",
      ctaPath: SUBSCRIPTION_PATH,
    });
  }

  if (hasExpiredSchoolLicense(user)) {
    return basePresentation({
      variant: PRODUCT_ACCESS_GATE_VARIANTS.SCHOOL_EXPIRED,
      contextEyebrow: "You're signed in",
      contextHeadline: "Your school’s Lesson Launchpad access has ended.",
      contextMessage:
        "You’re still signed in, but your school license is no longer active. Nothing is broken — your classes and content are saved. You can see what’s new, explore individual plans, or get school licensing info.",
      reactivationHeadline: "You might not recognize Lesson Launchpad anymore.",
      reactivationMessage:
        "The Lesson Launchpad you used has grown into a system that can run your routines, schedule your day, and keep your classes moving automatically.",
      primaryCta: {
        label: "See what's new",
        path: WHATS_NEW_PATH,
      },
      secondaryCta: {
        label: "View individual plans",
        path: PRICING_PATH,
      },
      tertiaryCta: {
        label: "School licensing info",
        path: SCHOOL_ADMIN_PATH,
      },
      closingHeadline: "Ready to subscribe?",
      closingMessage: "Skip the preview and get full access immediately.",
      closingPrimaryCta: {
        label: "View Plans",
        path: PRICING_PATH,
      },
      closingSecondaryCta: {
        label: "View individual plans",
        path: PRICING_PATH,
      },
      contactCta: {
        label: "Contact support",
        path: CONTACT_PATH,
      },
      headline: "Your school’s Lesson Launchpad access has ended.",
      message:
        "You’re still signed in, but your school license is no longer active. Nothing is broken — your classes and content are saved.",
      ctaLabel: "View individual plans",
      ctaPath: PRICING_PATH,
    });
  }

  if (hasPaidHistory(user) || isChurned(user)) {
    return basePresentation({
      variant: PRODUCT_ACCESS_GATE_VARIANTS.CHURNED,
      contextEyebrow: "You're signed in",
      contextHeadline: "Your subscription has ended.",
      contextMessage:
        "You’re still signed in, but this account no longer has active access to Lesson Launchpad. Nothing is broken — see what’s changed since you left, choose a plan to come back, or sign out and use another account.",
      reactivationHeadline: "A lot has changed since you left.",
      reactivationMessage:
        "The Lesson Launchpad you used has grown into a system that can run your routines, schedule your day, and keep your classes moving automatically.",
      primaryCta: {
        label: "See what's new",
        path: WHATS_NEW_PATH,
      },
      secondaryCta: {
        label: "View plans",
        path: PRICING_PATH,
      },
      tertiaryCta: {
        label: "Manage subscription",
        path: SUBSCRIPTION_PATH,
      },
      closingHeadline: "Ready to subscribe?",
      closingMessage: "Skip the preview and get full access immediately.",
      closingPrimaryCta: {
        label: "View Plans",
        path: PRICING_PATH,
      },
      closingSecondaryCta: {
        label: "View plans",
        path: PRICING_PATH,
      },
      headline: "Your subscription has ended.",
      message:
        "You’re still signed in, but this account no longer has active access to Lesson Launchpad. Nothing is broken.",
      ctaLabel: "View plans",
      ctaPath: PRICING_PATH,
    });
  }

  return basePresentation({
    variant: PRODUCT_ACCESS_GATE_VARIANTS.TRIAL_EXPIRED,
    contextEyebrow: "You're signed in",
    contextHeadline: "Your trial has ended.",
    contextMessage:
      "You’re signed in to a Lesson Launchpad account that no longer has access to the app. Nothing is broken — you can see what’s changed, choose a plan to get back in, or sign out and use a different account.",
    reactivationHeadline: "You might not recognize Lesson Launchpad anymore.",
    reactivationMessage:
      "The Lesson Launchpad  you tried has grown into a system that can run your routines, schedule your day, and keep your classes moving automatically.",
    primaryCta: {
      label: "See what's new",
      path: WHATS_NEW_PATH,
    },
    secondaryCta: null,
    tertiaryCta: null,
    closingHeadline: "Ready to subscribe?",
    closingMessage: "Skip the preview and get full access immediately.",
    closingPrimaryCta: {
      label: "View Plans",
      path: PRICING_PATH,
    },
    closingSecondaryCta: {
      label: "View plans",
      path: PRICING_PATH,
    },
    headline: "Your trial has ended.",
    message:
      "You’re signed in to a Lesson Launchpad account that no longer has access to the app. Nothing is broken.",
    ctaLabel: "View plans",
    ctaPath: PRICING_PATH,
  });
}

/** @deprecated Prefer getProductAccessGatePresentation for the no-access UI. */
export function getPaywallPresentation(user, options = {}) {
  const presentation = getProductAccessGatePresentation(user, options);
  return {
    headline: presentation.headline,
    message: presentation.message,
    ctaLabel: presentation.ctaLabel,
    ctaPath: presentation.ctaPath,
    variant: presentation.variant,
  };
}
